import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { WorkspaceProvider, useWorkspace, loadSavedSession, clearSavedSession } from '../context/WorkspaceContext';
import { PreferencesProvider, usePreferences } from '../context/PreferencesContext';
import { useVoiceAgent } from '../hooks/useVoiceAgent';
import { useStructuredChat } from '../hooks/useStructuredChat';
import WorkspaceHeader from '../components/workspace/WorkspaceHeader';
import ConversationPanel from '../components/workspace/ConversationPanel';
import DocumentPanel from '../components/document/DocumentPanel';
import MatchingPanel from '../components/matching/MatchingPanel';
import PreferencesModal from '../components/workspace/PreferencesModal';
import { ToastProvider, useToast } from '../components/workspace/Toast';
import CompletionScreen from '../components/workspace/CompletionScreen';
import GuidedTour from '../components/workspace/GuidedTour';
import { buildTourSteps, TOUR_SEEN_KEY } from '../components/workspace/tourSteps';
import '../styles/workspace.css';
import '../styles/humanaize-theme.css';

/* ── Inner component that uses workspace hooks ── */
function WorkspaceContent() {
  const { state, dispatch } = useWorkspace();
  const voice = useVoiceAgent();
  const structured = useStructuredChat();
  const showToast = useToast();
  const { hasConfigured, persona } = usePreferences();
  // Rebuild tour steps whenever the persona name changes — keeps "Dit ben ik: Job"
  // and "Dit ben ik: Jasmijn" in sync with the active voice.
  const tourSteps = useMemo(() => buildTourSteps(persona), [persona]);

  const [activeTab, setActiveTab] = useState('conversation'); // 'conversation' | 'document'
  const [showRestore, setShowRestore] = useState(() => !!loadSavedSession());
  const [showCompletion, setShowCompletion] = useState(false);
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const prefsAutoOpenedRef = useRef(false);
  const [tourOpen, setTourOpen] = useState(false);
  const tourAutoOpenedRef = useRef(false);

  // Open tour explicitly (from header ? button); also clears the seen flag
  // so dismissing the tour later won't re-trigger auto-open.
  const handleOpenTour = useCallback(() => {
    setTourOpen(true);
  }, []);
  const handleCloseTour = useCallback(() => {
    setTourOpen(false);
    try { localStorage.setItem(TOUR_SEEN_KEY, '1'); } catch {}
  }, []);
  const completionShownRef = useRef(false);
  const voiceStartIndexRef = useRef(-1); // index into messages when voice started
  const lastSyncIndexRef = useRef(-1); // tracks what we've already synced
  const syncInFlightRef = useRef(false);
  const messagesRef = useRef(state.conversation.messages);
  messagesRef.current = state.conversation.messages;

  const [urlInput, setUrlInput] = useState('');
  const [showUrlInput, setShowUrlInput] = useState(false);
  const hiddenFileInputRef = useRef(null);
  const [pendingFirstAction, setPendingFirstAction] = useState(null);

  /* ── Live voice → document sync (every ~4 new voice messages) ── */
  useEffect(() => {
    if (voice.status !== 'connected') return;
    if (voiceStartIndexRef.current < 0) return;
    if (syncInFlightRef.current) return;

    const msgs = state.conversation.messages;
    const startIdx = voiceStartIndexRef.current;
    const lastSync = lastSyncIndexRef.current >= 0 ? lastSyncIndexRef.current : startIdx;
    const newMessages = msgs.slice(lastSync).filter(m => m.sender === 'user' || m.sender === 'ai');

    // Trigger sync every 4 new voice messages (roughly 2 complete exchanges)
    if (newMessages.length >= 4) {
      syncInFlightRef.current = true;
      const allVoiceMsgs = msgs.slice(startIdx).filter(m => m.sender === 'user' || m.sender === 'ai');
      structured.syncVoiceProgress(allVoiceMsgs).finally(() => {
        lastSyncIndexRef.current = msgs.length;
        syncInFlightRef.current = false;
      });
    }
  }, [voice.status, state.conversation.messages, structured]);

  /* ── Warn before closing tab when document has content ── */
  useEffect(() => {
    const hasContent = Object.values(state.document.sections).some(s => s.status !== 'empty');
    if (!hasContent) return;

    function handleBeforeUnload(e) {
      e.preventDefault();
      // Modern browsers ignore custom messages but still show a confirmation dialog
      e.returnValue = '';
    }
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [state.document.sections]);

  /* ── Responsive detection ── */
  useEffect(() => {
    function handleResize() {
      dispatch({ type: 'SET_MOBILE', payload: window.innerWidth <= 768 });
    }

    handleResize(); // set initial value
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [dispatch]);

  const { isMobile } = state.ui;
  const isTablet = !isMobile && window.innerWidth <= 1024;

  /* ── Event handlers ── */
  const handleSendText = useCallback((text) => {
    if (voice.status === 'connected') {
      // Voice session active: send through ElevenLabs only
      // The agent responds with voice, keeping a single conversation thread
      voice.sendText(text);
    } else {
      // No voice session: fall back to structured backend
      structured.sendMessage(text);
    }
  }, [voice, structured]);

  const handleCardRespond = useCallback((cardId, response) => {
    // Card responses go to structured backend (for document updates)
    structured.sendCardResponse(cardId, response);
    // Also silently inform voice agent of the user's choice
    if (voice.status === 'connected') {
      voice.sendContext(`Gebruiker beantwoordde kaart ${cardId}: ${response}`);
    }
  }, [voice, structured]);

  // Live top-match → voice agent, zodat de agent dezelfde naam kan noemen als het
  // scherm toont. Guard op naam zodat we niet bij elke render herinjecteren.
  const lastInjectedHeroRef = useRef(null);
  const handleHeroChange = useCallback((m) => {
    if (voice.status !== 'connected' || !m?.name) return;
    if (m.name === lastInjectedHeroRef.current) return;
    lastInjectedHeroRef.current = m.name;
    const pct = typeof m.score === 'number'
      ? (m.score <= 1 ? Math.round(m.score * 100) : Math.round(m.score))
      : null;
    voice.sendContext(
      `Interne update (niet letterlijk voorlezen): de beste match op het scherm is nu ${m.name}` +
      (m.role ? ` (${m.role})` : '') +
      (pct != null ? `, ${pct}% match` : '') +
      `. Noem ${m.name} als de gebruiker vraagt wie je aanraadt of als je afrondt, met in één zin waarom die past.`
    );
  }, [voice]);

  const handleStartVoice = useCallback(async () => {
    try {
      voiceStartIndexRef.current = state.conversation.messages.length;
      lastSyncIndexRef.current = -1;
      await voice.start();
      showToast('Microfoon actief', 'success');
    } catch (err) {
      console.error('[WorkspacePage] Failed to start voice:', err);
      voiceStartIndexRef.current = -1;
      showToast(
        err.message?.includes('permission')
          ? 'Microfoon niet beschikbaar, controleer je browserinstellingen'
          : 'Spraak starten mislukt',
        'error'
      );
    }
  }, [voice, state.conversation.messages.length, showToast]);

  const handleStopVoice = useCallback(async () => {
    await voice.stop();
    showToast('Spraak gestopt', 'info');

    // Extract voice session messages and send to structured backend
    // This achieves two things:
    // 1. Backend extracts document updates from the voice conversation
    // 2. Backend session history gets seeded, so text messages have context
    const startIdx = voiceStartIndexRef.current;
    if (startIdx >= 0) {
      const voiceMessages = messagesRef.current
        .slice(startIdx)
        .filter(m => m.sender === 'user' || m.sender === 'ai');

      if (voiceMessages.length > 0) {
        // Silently sync document updates, no loading state or extra messages
        structured.processVoiceTranscript(voiceMessages);
      }
    }
    voiceStartIndexRef.current = -1;
    lastSyncIndexRef.current = -1;
  }, [voice, structured, showToast]);

  const handleStart = useCallback(() => {
    const sessionId = `session-${Date.now()}`;
    dispatch({ type: 'START_SESSION', payload: sessionId });
    // Show hardcoded welcome message instantly (no API call)
    structured.initSession();
  }, [dispatch, structured]);

  /* AIVD matcher: skip the intro screen — auto-start the session on mount so
     the chat shows immediately. */
  const autoStartedRef = useRef(false);
  useEffect(() => {
    if (autoStartedRef.current) return;
    if (state.ui.hasStarted) return;
    if (showRestore) return; // wait for user choice if a saved session exists
    autoStartedRef.current = true;
    handleStart();
  }, [state.ui.hasStarted, showRestore, handleStart]);

  const handleUploadCardClick = useCallback(() => {
    hiddenFileInputRef.current?.click();
  }, []);

  const handleHiddenFileChange = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPendingFirstAction({ type: 'file', file });
    handleStart();
    // Clear input so selecting the same file again still fires change
    e.target.value = '';
  }, [handleStart]);

  const handleUrlCardSubmit = useCallback(() => {
    const url = urlInput.trim();
    if (!url) return;
    setPendingFirstAction({
      type: 'url',
      text: `Begin met de informatie van deze bedrijfspagina: ${url}`,
    });
    handleStart();
    setUrlInput('');
  }, [urlInput, handleStart]);

  const handleRestore = useCallback(() => {
    const saved = loadSavedSession();
    if (saved) {
      dispatch({ type: 'RESTORE_SESSION', payload: saved });
      showToast('Vorige sessie hersteld', 'success');
    }
    setShowRestore(false);
  }, [dispatch, showToast]);

  const handleDismissRestore = useCallback(() => {
    clearSavedSession();
    setShowRestore(false);
  }, []);

  const [showResetConfirm, setShowResetConfirm] = useState(false);

  const handleReset = useCallback(() => {
    setShowResetConfirm(true);
  }, []);

  const handleResetConfirm = useCallback(() => {
    setShowResetConfirm(false);
    if (voice.status === 'connected') voice.stop();
    dispatch({ type: 'RESET_SESSION' });
    structured.resetFirstConceptToast();
    completionShownRef.current = false;
    setShowCompletion(false);
  }, [dispatch, voice, structured]);

  const handleUploadFile = useCallback(async (file) => {
    const result = await structured.uploadFile(file);
    // If voice session is active, push the document updates as context
    // so the ElevenLabs agent knows about the uploaded content
    if (voice.status === 'connected' && result?.documentUpdates?.length) {
      const sections = result.documentUpdates
        .map(u => `${u.section}: ${u.content}`)
        .join('\n');
      voice.sendContext(
        `De gebruiker heeft een bestand geüpload: "${file.name}". ` +
        `De volgende vacaturesecties zijn bijgewerkt:\n${sections}\n` +
        `Ga verder met het gesprek en verwijs naar deze nieuwe informatie.`
      );
    }
  }, [structured, voice]);

  /* AIVD matcher: preferences modal disabled (HAI Job vacancy-style copy). */
  // useEffect(() => {
  //   if (state.ui.hasStarted && !hasConfigured && !prefsAutoOpenedRef.current) {
  //     prefsAutoOpenedRef.current = true;
  //     setPreferencesOpen(true);
  //   }
  // }, [state.ui.hasStarted, hasConfigured]);

  /* AIVD matcher: guided tour disabled (was HAI Job-flavored). The ? icon in
     the header now opens a context-appropriate help popover instead. */
  // useEffect(() => {
  //   if (!state.ui.hasStarted || preferencesOpen || tourAutoOpenedRef.current) return;
  //   ...
  // }, [state.ui.hasStarted, preferencesOpen]);

  /* ── Run pending first action once session has started (upload / URL seed) ── */
  useEffect(() => {
    if (!state.ui.hasStarted || !pendingFirstAction) return;

    if (pendingFirstAction.type === 'file') {
      handleUploadFile(pendingFirstAction.file);
    } else if (pendingFirstAction.type === 'url') {
      structured.sendMessage(pendingFirstAction.text);
    }
    setPendingFirstAction(null);
  }, [state.ui.hasStarted, pendingFirstAction, handleUploadFile, structured]);

  const handleRegenerate = useCallback((sectionKey, action) => {
    showToast('Sectie wordt herschreven...', 'info');
    structured.regenerateSection(sectionKey, action);
  }, [structured, showToast]);

  /* ── Show completion screen once when 100%, auto-stop voice ──
     The earlier userConfirmedConcept gate was too strict: voice-sync fills
     sections incrementally (1-2 per sync), never as a 4+ batch, so the flag
     never flipped true and the popup never fired despite 100% completion.
     Removed the gate, premature-popup protection now lives in the prompts:
     chat.js Fase-2 concept-checkpoint + ElevenLabs CONCEPT-CHECKPOINT block
     both require explicit user confirmation before the AI fills all sections. */
  useEffect(() => {
    if (
      state.document.completionPercentage === 100 &&
      !completionShownRef.current &&
      state.ui.hasStarted
    ) {
      completionShownRef.current = true;
      // Auto-stop voice session so user can review in peace
      if (voice.status === 'connected') {
        voice.stop();
      }
      setShowCompletion(true);
    }
  }, [state.document.completionPercentage, state.ui.hasStarted, voice]);

  /* ── Keyboard shortcuts ── */
  useEffect(() => {
    function handleKeyDown(e) {
      if (e.key === 'Escape' && voice.status === 'connected') {
        e.preventDefault();
        handleStopVoice();
      }
      if (e.key === '/' && !['INPUT', 'TEXTAREA'].includes(document.activeElement?.tagName)) {
        e.preventDefault();
        document.querySelector('.input-bar__text')?.focus();
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [voice.status, handleStopVoice]);

  /* ── Intro screen ── */
  if (!state.ui.hasStarted) {
    return (
      <div className="workspace-page">
        <WorkspaceHeader voiceStatus={voice.status} isSpeaking={voice.isSpeaking} hasUserSpoken={voice.hasUserSpoken} onOpenReview={() => setShowCompletion(true)} onReset={handleReset} onOpenPreferences={undefined} onOpenTour={undefined} />
        <div className="workspace-intro">
          {showRestore ? (
            <>
              <span className="workspace-intro__icon material-icons" aria-hidden="true">history</span>
              <h1 className="workspace-intro__title">
                Je hebt een onafgeronde zoekopdracht
              </h1>
              <p className="workspace-intro__subtitle">
                Wil je verder gaan waar je gebleven was, of opnieuw beginnen?
              </p>
              <div className="workspace-intro__actions">
                <button
                  type="button"
                  className="workspace-intro__start-btn"
                  onClick={handleRestore}
                >
                  <span className="material-icons" aria-hidden="true">play_arrow</span>
                  Verder gaan
                </button>
                <button
                  type="button"
                  className="workspace-intro__secondary-btn"
                  onClick={handleDismissRestore}
                >
                  Opnieuw beginnen
                </button>
              </div>
            </>
          ) : (
            <>
              <span className="workspace-intro__eyebrow">
                Humanaize · De Matcher
              </span>
              <h1 className="workspace-intro__title">
                Vind in <span className="workspace-intro__title-accent">5 minuten</span> de juiste student-engineer
              </h1>
              <p className="workspace-intro__subtitle">
                Voor recruiters en projectleiders.
                <br />
                Praat, typ of dicteer waar je iemand voor zoekt.
              </p>

              <div className="workspace-intro__card-grid">
                <button
                  type="button"
                  className="workspace-intro__card workspace-intro__card--primary"
                  onClick={handleStart}
                >
                  <span className="workspace-intro__card-num" aria-hidden="true">01</span>
                  <span className="material-icons workspace-intro__card-icon" aria-hidden="true">forum</span>
                  <span className="workspace-intro__card-title">Ik vertel</span>
                  <span className="workspace-intro__card-desc">Begin met praten of typen</span>
                </button>
              </div>

              <p className="workspace-intro__tip">
                <span className="material-icons workspace-intro__tip-icon" aria-hidden="true">info</span>
                Tip: werk in een rustige omgeving zonder achtergrondgeluid, dan verstaat de AI je het best.
              </p>

              <input
                ref={hiddenFileInputRef}
                type="file"
                accept=".pdf,.docx,.txt"
                style={{ display: 'none' }}
                onChange={handleHiddenFileChange}
              />
            </>
          )}
        </div>
      </div>
    );
  }

  /* ── Main workspace ── */
  return (
    <div className="workspace-page">
      <WorkspaceHeader voiceStatus={voice.status} isSpeaking={voice.isSpeaking} hasUserSpoken={voice.hasUserSpoken} onOpenReview={() => setShowCompletion(true)} onReset={handleReset} onOpenPreferences={undefined} onOpenTour={undefined} />

      {/* Tablet tab toggle */}
      {isTablet && (
        <div className="tablet-tabs">
          <button
            type="button"
            className={`tablet-tabs__btn ${activeTab === 'conversation' ? 'tablet-tabs__btn--active' : ''}`}
            onClick={() => setActiveTab('conversation')}
          >
            Gesprek
          </button>
          <button
            type="button"
            className={`tablet-tabs__btn ${activeTab === 'document' ? 'tablet-tabs__btn--active' : ''}`}
            onClick={() => setActiveTab('document')}
          >
            Matches
          </button>
        </div>
      )}

      <div className="workspace-layout">
        <ConversationPanel
          className={isTablet && activeTab !== 'conversation' ? 'conversation-panel--hidden' : ''}
          onCardRespond={handleCardRespond}
          onSendText={handleSendText}
          onUploadFile={handleUploadFile}
          onStartVoice={handleStartVoice}
          onStopVoice={handleStopVoice}
          voiceStatus={voice.status}
          isSpeaking={voice.isSpeaking}
          isListening={voice.isListening}
          hasUserSpoken={voice.hasUserSpoken}
        />

        <MatchingPanel
          className={
            isMobile
              ? 'document-panel--hidden'
              : isTablet && activeTab !== 'document'
                ? 'document-panel--hidden'
                : ''
          }
          voiceStatus={voice.status}
          onHeroChange={handleHeroChange}
        />
      </div>

      {/* AIVD matcher: guided tour disabled */}
      {false && <GuidedTour open={tourOpen} onClose={handleCloseTour} steps={tourSteps} />}

      {/* Mobile: floating toggle + slide-up sheet */}
      {isMobile && (
        <>
          <button
            type="button"
            className="mobile-doc-toggle"
            onClick={() => dispatch({ type: 'TOGGLE_DOCUMENT_SHEET' })}
            aria-label="Toon shortlist"
          >
            <span className="material-icons" aria-hidden="true">description</span>
          </button>

          <div className={`mobile-doc-sheet ${state.ui.documentSheetOpen ? 'mobile-doc-sheet--open' : ''}`}>
            <div className="mobile-doc-sheet__handle" />
            <button
              type="button"
              className="mobile-doc-sheet__close"
              onClick={() => dispatch({ type: 'TOGGLE_DOCUMENT_SHEET' })}
              aria-label="Sluit document"
            >
              <span className="material-icons" aria-hidden="true">close</span>
            </button>
            <MatchingPanel voiceStatus={voice.status} onHeroChange={handleHeroChange} />
          </div>
        </>
      )}

      {showCompletion && (
        <CompletionScreen
          onDismiss={() => setShowCompletion(false)}
          onEditSection={(sectionKey, instruction) => {
            return structured.editSection(sectionKey, instruction);
          }}
        />
      )}

      {showResetConfirm && createPortal(
        <div className="ws-confirm">
          <div className="ws-confirm__backdrop" onClick={() => setShowResetConfirm(false)} />
          <div className="ws-confirm__card">
            <span className="ws-confirm__icon material-icons">warning_amber</span>
            <h2 className="ws-confirm__title">Opnieuw beginnen?</h2>
            <p className="ws-confirm__text">
              Je huidige zoekopdracht en gesprek gaan verloren. Dit kan niet ongedaan worden.
            </p>
            <div className="ws-confirm__actions">
              <button
                type="button"
                className="ws-confirm__btn ws-confirm__btn--cancel"
                onClick={() => setShowResetConfirm(false)}
              >
                Annuleren
              </button>
              <button
                type="button"
                className="ws-confirm__btn ws-confirm__btn--danger"
                onClick={handleResetConfirm}
              >
                <span className="material-icons">restart_alt</span>
                Begin opnieuw
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* AIVD matcher: preferences modal disabled */}
      {false && <PreferencesModal open={preferencesOpen} onClose={() => setPreferencesOpen(false)} />}
    </div>
  );
}

/* ── Outer wrapper with Provider ── */
export default function WorkspacePage() {
  return (
    <PreferencesProvider>
      <WorkspaceProvider>
        <ToastProvider>
          <WorkspaceContent />
        </ToastProvider>
      </WorkspaceProvider>
    </PreferencesProvider>
  );
}
